import { createRoute, useNavigate } from "@tanstack/react-router";
import { Mail02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { rootRoute } from "./root-route";

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo:
      typeof search.returnTo === "string" ? search.returnTo : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: LoginPage,
});

function errorFromUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get("error");
  } catch {
    return null;
  }
}

function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(() =>
    errorFromUrl() === "not_allowed"
      ? "该邮箱不在白名单中。"
      : null,
  );
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (auth.status === "authed") {
      void navigate({ to: "/" });
    }
  }, [auth.status, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setError(null);
    setInvalid(false);
    setSent(false);
    setDevLink(null);
    try {
      const returnTo =
        new URLSearchParams(window.location.search).get("returnTo") ?? undefined;
      const res = await api.magicStart(email.trim(), returnTo);
      if (res.devEchoLink) setDevLink(res.devEchoLink);
      else setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === "not_allowed") {
        setError("该邮箱不在白名单中。");
        setInvalid(true);
      } else if (err instanceof ApiError && err.code === "rate_limited") {
        setError("尝试次数过多，请一分钟后再试。");
      } else {
        setError("出了点问题，请重试。");
      }
    } finally {
      setBusy(false);
    }
  };

  if (auth.status === "loading" || auth.status === "authed") {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>登录 Peekie Dash</CardTitle>
          <CardDescription>
            仅白名单邮箱可通过魔法链接登录。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit}>
            <FieldGroup>
              <Field data-invalid={invalid || undefined}>
                <FieldLabel htmlFor="email">邮箱</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                  placeholder="you@example.com"
                  value={email}
                  aria-invalid={invalid || undefined}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <FieldDescription>
                  我们会发送一次性登录链接。无需密码。如需开通权限，请联系管理员。
                </FieldDescription>
              </Field>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? (
                  <Spinner />
                ) : (
                  <HugeiconsIcon
                    icon={Mail02Icon}
                    data-icon="inline-start"
                  />
                )}
                {busy ? "发送中…" : "发送登录链接"}
              </Button>
            </FieldGroup>
          </form>

          {devLink ? (
            <Alert className="mt-4">
              <AlertDescription>
                开发模式：
                <a
                  className="font-medium underline underline-offset-2"
                  href={devLink}
                >
                  点击登录
                </a>
              </AlertDescription>
            </Alert>
          ) : null}
          {sent ? (
            <Alert className="mt-4">
              <AlertDescription>
                请查收邮件中的登录链接。
              </AlertDescription>
            </Alert>
          ) : null}
          {error ? (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
